import { useRef, useState } from "react";
import { CheckCircle2, AlertTriangle, Lightbulb, TrendingUp, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { AspectRatio } from "@/components/ui/aspect-ratio";

interface ProjectResult {
  value: string;
  label: string;
}

interface ProjectCardProps {
  project: {
    id: string;
    title: string;
    subtitle: string;
    category: string;
    status?: "active" | "development";
    description: string;
    challenges: string[];
    solutions: string[];
    results: ProjectResult[];
    media?: {
      type: "video" | "image";
      src: string;
      gallery?: string[];
    };
  };
}

const ProjectCard = ({ project }: ProjectCardProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const handleMouseEnter = () => {
    if (videoRef.current) {
      videoRef.current.play();
    }
  };

  const handleMouseLeave = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  const gallery = project.media?.gallery || (project.media?.src ? [project.media.src] : []);
  const hasMultipleImages = gallery.length > 1;

  const nextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % gallery.length);
  };

  const prevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + gallery.length) % gallery.length);
  };

  return (
    <div 
      className="bg-card border border-border rounded-2xl overflow-hidden hover:shadow-lg hover:border-primary/30 transition-all duration-300"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Media Thumbnail */}
      {project.media && (
        <div className="relative overflow-hidden group">
          <AspectRatio ratio={16 / 9}>
            {project.media.type === "video" ? (
              <video
                ref={videoRef}
                src={project.media.src}
                muted
                loop
                playsInline
                className="w-full h-full object-cover"
              />
            ) : (
              <img
                src={gallery[currentImageIndex]}
                alt={`${project.title} - Image ${currentImageIndex + 1}`}
                className="w-full h-full object-cover transition-all duration-300"
              />
            )}
          </AspectRatio>

          {/* Gallery Navigation */}
          {hasMultipleImages && project.media.type === "image" && (
            <>
              {/* Arrow buttons */}
              <button
                onClick={prevImage}
                className="absolute left-2 top-1/2 -translate-y-1/2 z-10 p-1.5 rounded-full bg-background/80 text-foreground opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-background"
                aria-label="Previous image"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-2 top-1/2 -translate-y-1/2 z-10 p-1.5 rounded-full bg-background/80 text-foreground opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-background"
                aria-label="Next image"
              >
                <ChevronRight className="w-4 h-4" />
              </button>

              {/* Dot indicators */}
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex gap-1.5">
                {gallery.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentImageIndex(idx);
                    }}
                    className={`w-2 h-2 rounded-full transition-all duration-200 ${
                      idx === currentImageIndex 
                        ? "bg-primary scale-110" 
                        : "bg-background/60 hover:bg-background/80"
                    }`}
                    aria-label={`Go to image ${idx + 1}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Header */}
      <div className="p-5 border-b border-border bg-secondary/30">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <h3 className="font-bold text-lg text-foreground leading-tight">{project.title}</h3>
            <p className="text-sm text-muted-foreground">{project.subtitle}</p>
          </div>
          <span className="flex-shrink-0 px-3 py-1 rounded-full bg-primary/20 text-primary text-xs font-medium">
            {project.category}
          </span>
        </div>
        <p className="text-sm text-muted-foreground line-clamp-2">{project.description}</p>
      </div>

      {/* Content Grid */}
      <div className="p-5 space-y-4">
        {/* Challenges */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-destructive" />
            <span className="text-xs font-semibold text-foreground uppercase tracking-wide">Challenges</span>
          </div>
          <ul className="space-y-1">
            {project.challenges.slice(0, 3).map((challenge, idx) => (
              <li key={idx} className="flex items-start gap-2 text-xs text-muted-foreground">
                <span className="w-1 h-1 rounded-full bg-destructive mt-1.5 flex-shrink-0" />
                <span className="line-clamp-1">{challenge}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Solutions */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="w-4 h-4 text-primary" />
            <span className="text-xs font-semibold text-foreground uppercase tracking-wide">Solutions</span>
          </div>
          <ul className="space-y-1">
            {project.solutions.slice(0, 3).map((solution, idx) => (
              <li key={idx} className="flex items-start gap-2 text-xs text-muted-foreground">
                <CheckCircle2 className="w-3 h-3 text-primary mt-0.5 flex-shrink-0" />
                <span className="line-clamp-1">{solution}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Results */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-chart-2" />
            <span className="text-xs font-semibold text-foreground uppercase tracking-wide">Results</span>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {project.results.map((result, idx) => (
              <div key={idx} className="text-center p-2 rounded-lg bg-secondary/50">
                <div className="text-sm font-bold text-primary">{result.value}</div>
                <div className="text-[10px] text-muted-foreground leading-tight">{result.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-5 pb-5">
        <Button variant="outline" size="sm" className="w-full" asChild>
          <Link to="/contact">
            Discuss Project <ArrowRight className="w-3 h-3 ml-1" />
          </Link>
        </Button>
      </div>
    </div>
  );
};

export default ProjectCard;
